import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Camera, Plus, Copy, Send, CheckCircle, Calendar,
  ExternalLink, Eye, EyeOff, Image as ImageIcon, X
} from "lucide-react";

export default function DogUpdatesPanel({ dogId, dogName }) {
  const [updates, setUpdates] = useState([]);
  const [guardianLink, setGuardianLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState(null);

  // Form state
  const [imageBase64, setImageBase64] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [note, setNote] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [prepareWhatsapp, setPrepareWhatsapp] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (dogId) fetchUpdates();
  }, [dogId]);

  const fetchUpdates = async () => {
    try {
      const res = await axios.get(`${API}/admin/dogs/${dogId}/updates`, { withCredentials: true });
      setUpdates(res.data.updates || []);
      setGuardianLink(res.data.guardian_link || "");
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Dosya 5MB'den kucuk olmali");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageBase64(reader.result);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!imageBase64) {
      toast.error("Fotograf yukleyin");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/dogs/${dogId}/updates`, {
        image_base64: imageBase64,
        note: note.trim() || null,
        visibility,
        prepare_whatsapp: prepareWhatsapp
      }, { withCredentials: true });

      toast.success("Güncelleme eklendi");
      setShareData(res.data);
      setShowForm(false);
      setShowShareModal(true);

      // Reset form
      setImageBase64("");
      setImagePreview("");
      setNote("");
      setVisibility("public");
      fetchUpdates();
    } catch (e) {
      toast.error("Güncelleme eklenemedi");
    } finally {
      setSubmitting(false);
    }
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} kopyalandi!`);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-10"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="dog-updates-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg">Güncellemeler</h3>
        <Button size="sm" onClick={() => setShowForm(true)} data-testid="add-update-btn">
          <Plus className="w-4 h-4 mr-1" /> Güncelleme Ekle
        </Button>
      </div>

      {/* Guardian Link Quick Copy */}
      {guardianLink && (
        <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-xl text-sm">
          <ExternalLink className="w-4 h-4 text-primary shrink-0" />
          <span className="text-muted-foreground truncate flex-1">Koruyucu aile linki: {guardianLink}</span>
          <Button size="sm" variant="outline" className="shrink-0" onClick={() => copyText(guardianLink, "Link")} data-testid="copy-guardian-link-btn">
            <Copy className="w-3.5 h-3.5 mr-1" /> Kopyala
          </Button>
        </div>
      )}

      {/* Updates List */}
      {updates.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
          <Camera className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Henüz güncelleme yok. Koruyucu ailelere paylaşmak için güncelleme ekleyin.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {updates.map((u) => (
            <div key={u.update_id} className="flex gap-3 p-3 bg-muted/20 rounded-xl" data-testid={`admin-update-${u.update_id}`}>
              <img src={u.image_base64} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                {u.note && <p className="text-sm line-clamp-2">{u.note}</p>}
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(u.created_at).toLocaleDateString("tr-TR")}
                  </span>
                  {u.visibility === "guardian_only" ? (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0"><EyeOff className="w-3 h-3 mr-0.5" />Ozel</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0"><Eye className="w-3 h-3 mr-0.5" />Herkese acik</Badge>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Update Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-md" data-testid="add-update-dialog">
          <DialogTitle className="font-heading text-xl">Güncelleme Ekle</DialogTitle>
          <div className="space-y-4">
            {/* Photo Upload */}
            <div>
              <Label>Fotograf <span className="text-destructive">*</span></Label>
              {imagePreview ? (
                <div className="relative mt-2">
                  <img src={imagePreview} alt="Onizleme" className="w-full h-48 object-cover rounded-xl" />
                  <Button
                    size="icon"
                    variant="destructive"
                    className="absolute top-2 right-2 h-7 w-7"
                    onClick={() => { setImageBase64(""); setImagePreview(""); }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 w-full h-36 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-2 hover:border-primary/50 transition-colors cursor-pointer"
                  data-testid="upload-photo-area"
                >
                  <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
                  <span className="text-sm text-muted-foreground">Fotograf sec (max 5MB)</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
                data-testid="photo-file-input"
              />
            </div>

            {/* Note */}
            <div>
              <Label>Kisa not (opsiyonel, max 200 karakter)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 200))}
                placeholder="Bugun keyfi yerinde, istahi iyi"
                className="mt-1.5 resize-none"
                rows={2}
                data-testid="update-note-input"
              />
              <p className="text-xs text-muted-foreground text-right mt-1">{note.length}/200</p>
            </div>

            {/* Visibility */}
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
              <div className="flex items-center gap-2">
                {visibility === "public" ? <Eye className="w-4 h-4 text-primary" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
                <div>
                  <p className="text-sm font-medium">{visibility === "public" ? "Herkese acik" : "Sadece koruyucu ailelere ozel"}</p>
                  <p className="text-xs text-muted-foreground">{visibility === "public" ? "Kopek profilinde gorunur" : "Sadece ozel linkte gorunur"}</p>
                </div>
              </div>
              <Switch
                checked={visibility === "public"}
                onCheckedChange={(v) => setVisibility(v ? "public" : "guardian_only")}
                data-testid="visibility-toggle"
              />
            </div>

            {/* WhatsApp checkbox */}
            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-100 rounded-xl">
              <input
                type="checkbox"
                checked={prepareWhatsapp}
                onChange={(e) => setPrepareWhatsapp(e.target.checked)}
                className="rounded border-green-300"
                data-testid="whatsapp-checkbox"
              />
              <div>
                <p className="text-sm font-medium text-green-800">Koruyucu ailelere WhatsApp guncellemesi hazirla</p>
                <p className="text-xs text-green-600">Kaydet sonrasi kopyalanabilir mesaj olusturulur</p>
              </div>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={submitting || !imageBase64}
              className="w-full btn-pill bg-primary text-primary-foreground h-11"
              data-testid="save-and-share-btn"
            >
              {submitting ? "Kaydediliyor..." : "Kaydet ve Paylas"}
              <Send className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-md" data-testid="share-modal">
          <DialogTitle className="sr-only">Koruyucu Aile Güncellemesi Hazır</DialogTitle>
          <div className="space-y-5">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="font-heading text-xl">Koruyucu Aile Güncellemesi Hazır</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Aşağıdaki mesajı WhatsApp'ta koruyucu aile(ler)e gönderebilirsin.
              </p>
            </div>

            {/* Message box */}
            {shareData?.whatsapp_message && (
              <div className="p-4 bg-muted/30 rounded-xl border border-border/50">
                <p className="text-sm whitespace-pre-line break-all" data-testid="whatsapp-message-text">
                  {shareData.whatsapp_message}
                </p>
              </div>
            )}

            {/* Guardian list */}
            {shareData?.guardians?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium">Koruyucu aileler:</p>
                {shareData.guardians.map((g, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-muted/20 rounded-lg text-sm">
                    <span>{g.guardian_name}</span>
                    <span className="text-muted-foreground">{g.guardian_phone}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Buttons */}
            <div className="space-y-2">
              <Button
                className="w-full btn-pill bg-green-600 hover:bg-green-700 text-white h-11"
                onClick={() => copyText(shareData?.whatsapp_message || "", "Mesaj")}
                data-testid="copy-message-btn"
              >
                <Copy className="w-4 h-4 mr-2" /> Mesajı Kopyala
              </Button>
              <Button
                variant="outline"
                className="w-full btn-pill"
                onClick={() => copyText(shareData?.guardian_link || "", "Link")}
                data-testid="copy-link-btn"
              >
                <Copy className="w-4 h-4 mr-2" /> Linki Kopyala
              </Button>
              {shareData?.whatsapp_message && (
                <a
                  href={`https://web.whatsapp.com/send?text=${encodeURIComponent(shareData.whatsapp_message)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button variant="outline" className="w-full btn-pill text-green-700 border-green-200 hover:bg-green-50" data-testid="open-whatsapp-btn">
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp Web'de Ac
                  </Button>
                </a>
              )}
            </div>

            <Button variant="ghost" className="w-full" onClick={() => setShowShareModal(false)} data-testid="close-share-modal-btn">
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
